import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Boxes, Search, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import Input from "../../components/system/Input";
import EmptyState from "../../components/system/EmptyState";
import {
  OperatorTable,
  OperatorTableRow,
} from "../../components/system/OperatorTable";

/**
 * OperatorDepartmentsPage — `/platform/companies/:cid/departments`
 *
 * Tenant-wide departments list. Rows are clickable → department detail.
 * "Add department" dialog kept here (moved from inline list on
 * TenantDetailPage).
 */
export default function OperatorDepartmentsPage() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [companyName, setCompanyName] = useState("");
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [det, dp, us] = await Promise.all([
        api.get(`/platform/companies/${cid}`),
        api.get(`/platform/companies/${cid}/departments`),
        api.get(`/platform/companies/${cid}/users`),
      ]);
      setCompanyName(det.data.company?.name || "");
      setDepartments(dp.data.departments || []);
      setUsers(us.data.users || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps

  const userCountByDept = useMemo(() => {
    const map = new Map();
    for (const u of users) {
      if (u.department_id) {
        map.set(u.department_id, (map.get(u.department_id) || 0) + 1);
      }
    }
    return map;
  }, [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return departments;
    return departments.filter((d) =>
      (d.name || "").toLowerCase().includes(q),
    );
  }, [departments, search]);

  const addDept = async () => {
    const cleaned = addName.trim();
    if (!cleaned) return toast.error("Department name is required");
    setAdding(true);
    try {
      await api.post(`/platform/companies/${cid}/departments`, {
        name: cleaned,
      });
      toast.success("Department added");
      setAddOpen(false);
      setAddName("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setAdding(false);
    }
  };

  const GRID = "minmax(0,2fr) 1fr 1fr auto";

  return (
    <div data-testid="operator-departments-page">
      <div className="mb-4">
        <Link
          to={`/platform/companies/${cid}`}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink"
          data-testid="operator-departments-back"
        >
          <ArrowLeft size={14} />
          Back to tenant
        </Link>
      </div>

      <PageHeader
        eyebrow={`Tenant · ${companyName || cid}`}
        title="Departments"
        subtitle={`${departments.length} department${departments.length === 1 ? "" : "s"} in this tenant. Click a row to see its users and active batches.`}
        actions={
          <Button
            tier="primary"
            onClick={() => setAddOpen(true)}
            data-testid="operator-departments-add-button"
          >
            <Plus size={14} /> Add department
          </Button>
        }
      />

      <Card variant="raised" className="p-3 mb-4">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search department by name…"
            className="pl-9"
            data-testid="operator-departments-search"
          />
        </div>
      </Card>

      <OperatorTable
        gridTemplate={GRID}
        header={
          <>
            <div>Department</div>
            <div>Users</div>
            <div>ID</div>
            <div className="text-right">Action</div>
          </>
        }
        loading={loading}
        empty={!loading && filtered.length === 0}
        emptyState={
          <div className="p-10">
            <EmptyState
              icon={<Boxes size={32} />}
              title={search ? "No departments match" : "No departments yet"}
              description={
                search
                  ? "Adjust the search term or clear it."
                  : "Departments group products (e.g. Cheese, Bakery, Produce)."
              }
              data-testid="operator-departments-empty"
            />
          </div>
        }
        data-testid="operator-departments-table"
      >
        {filtered.map((d) => (
          <OperatorTableRow
            key={d.id}
            gridTemplate={GRID}
            onClick={() =>
              nav(`/platform/companies/${cid}/departments/${d.id}`)
            }
            data-testid={`operator-department-row-${d.id}`}
          >
            <div className="min-w-0 font-display font-semibold truncate">
              {d.name}
            </div>
            <div className="font-mono text-xs tabular-nums">
              {userCountByDept.get(d.id) || 0}
            </div>
            <div className="font-mono text-[11px] text-muted-foreground truncate">
              {d.id}
            </div>
            <div className="text-right">
              <Button
                tier="tertiary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  nav(`/platform/companies/${cid}/departments/${d.id}`);
                }}
                data-testid={`operator-department-open-${d.id}`}
              >
                Open
              </Button>
            </div>
          </OperatorTableRow>
        ))}
      </OperatorTable>

      {addOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 px-3"
          onClick={() => setAddOpen(false)}
          data-testid="operator-departments-add-dialog"
        >
          <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md">
            <Card variant="raised" className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="font-display font-black text-xl">
                  Add department
                </div>
                <button
                  onClick={() => setAddOpen(false)}
                  aria-label="Close"
                  className="p-2 -mt-2 -mr-2 rounded-button text-ink-2 hover:text-ink"
                  data-testid="operator-departments-add-close"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="data-sku block mb-1">Name *</label>
                  <Input
                    value={addName}
                    onChange={(e) => setAddName(e.target.value)}
                    placeholder="e.g. Cheese, Bakery, Produce"
                    autoFocus
                    data-testid="operator-departments-add-name"
                  />
                  <p className="font-mono text-[11px] text-muted-foreground mt-1">
                    Department names are unique per tenant.
                  </p>
                </div>
                <Button
                  tier="primary"
                  className="w-full"
                  onClick={addDept}
                  disabled={adding || !addName.trim()}
                  data-testid="operator-departments-add-submit"
                >
                  {adding ? "Creating…" : "Create department"}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
